import type { Ref } from "react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  charterValueIcon,
  charterValueOrdinal,
} from "@/lib/charter-value-icons"

/**
 * A single principle card — the brand's authored `charter_values` (or the
 * platform default, already resolved + localized server-side). Literal
 * copy, NOT i18n.
 */
export interface CharterValueCard {
  title: string
  body: string
  /** Catalog key → Lucide glyph; `null` → ordinal "01/02/03" badge. */
  icon_key: string | null
}

/**
 * OKS-384 / OKS-459 — Charter reframe header. Renders the framing above
 * the accept row:
 *
 *   1. An eyebrow `{BRAND} · Team values` (uppercased via CSS, NOT in
 *      source — a11y: screen readers read the brand name naturally).
 *   2. The values headline ("Here's how this team plays"), the page's
 *      focus target on mount (the page forwards its `headingRef`).
 *   3. A short subtitle.
 *   4. The three principle cards — OKS-459 makes these DATA-DRIVEN from
 *      the brand's `charter_values` (icon-chip + title + body), 1:1 and
 *      in order with the formal sheet's numbered blocks. Cards are
 *      informational only (non-focusable). The icon chip is decorative
 *      (`aria-hidden`); the title carries meaning. Long titles/bodies
 *      WRAP (no meaning-hiding truncation) so the edge state holds.
 */
export interface OnboardingPrincipleCardsProps {
  /** Brand name interpolated into the eyebrow + subtitle (uppercased via CSS on the eyebrow). */
  brandName: string
  /** The brand's three principle cards (server-resolved). */
  cards: CharterValueCard[]
  /** Forwarded to the headline so the page keeps focus-on-mount. */
  headingRef?: Ref<HTMLHeadingElement>
}

export function OnboardingPrincipleCards({
  brandName,
  cards,
  headingRef,
}: OnboardingPrincipleCardsProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4" data-testid="wizard-charter-values">
      <div className="flex flex-col gap-1">
        <p
          className="text-muted-foreground text-xs font-medium tracking-wide uppercase"
          data-testid="wizard-charter-eyebrow"
        >
          {t("wizard.charter.eyebrow_values", { brand: brandName })}
        </p>
        <h1
          ref={headingRef}
          tabIndex={-1}
          data-testid="wizard-step-charter-heading"
          className="text-2xl font-bold tracking-tight outline-none"
        >
          {t("wizard.charter.values_headline")}
        </h1>
        <p
          className="text-muted-foreground text-sm"
          data-testid="wizard-charter-subtitle"
        >
          {t("wizard.charter.values_subtitle", { brand: brandName })}
        </p>
      </div>

      <div
        className="grid grid-cols-1 gap-3 md:grid-cols-3"
        data-testid="wizard-charter-principles"
      >
        {cards.map((card, index) => {
          const Icon = charterValueIcon(card.icon_key)
          return (
            <Card
              key={index}
              data-testid={`wizard-charter-principle-${index}`}
              className="gap-3 py-4 transition-shadow hover:shadow-md"
            >
              <CardHeader className="flex flex-row items-start gap-3 px-4">
                <span
                  aria-hidden="true"
                  data-testid={`wizard-charter-principle-${index}-icon`}
                  className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                >
                  {Icon ? (
                    <Icon className="size-5" />
                  ) : (
                    charterValueOrdinal(index)
                  )}
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <CardTitle className="text-base break-words">
                    {card.title}
                  </CardTitle>
                  <CardDescription className="break-words">
                    {card.body}
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default OnboardingPrincipleCards
